import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import FormInputText from '../../../../common/Forms/FormTextField';
import TransDropdownFormInput from '../TransportUtils/TransportDropdownForm';
import { postStopApi, setStopApi } from '../../../../../services/StopApi/StopApi';
import { SCHEDULE_URL, formDateFormat, yesNoDropdown } from '../../../../../constants';

const modeOptions = [
  { id: 'Car', value: 'Car' },
  { id: 'Van', value: 'Van' },
  { id: 'Bus', value: 'Bus' },
  { id: 'Train', value: 'Train' },
  { id: 'Plane', value: 'Plane' },
  { id: 'Other', value: 'Other' },
];

const confirmedOptions = [
  { id: 'Yes', value: 'Yes' },
  { id: 'No', value: 'No' },
  { id: 'Pending', value: 'Pending' },
];

const ScheduleDialog = ({ open, setOpen, schedule, isNew, transportId, organizations, authToken, onSave }) => {
  const [saving, setSaving] = useState(false);

  const defaultValues = {
    departFrom: schedule?.departFrom || '',
    departDate: schedule?.departDate ? moment(schedule.departDate).format(formDateFormat) : '',
    departTime: schedule?.departTime || '',
    arrivedAt: schedule?.arrivedAt || '',
    arrivedDate: schedule?.arrivedDate ? moment(schedule.arrivedDate).format(formDateFormat) : '',
    arrivedTime: schedule?.arrivedTime || '',
    mode: schedule?.mode || '',
    confirmed: schedule?.confirmed || '',
  };

  const { control, register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues,
    mode: 'onBlur',
  });

  useEffect(() => {
    reset(defaultValues);
  }, [schedule]);

  const handleClose = () => {
    setOpen(false);
    reset(defaultValues);
  };

  const onSubmit = async (formData) => {
    setSaving(true);

    const payload = {
      sheduleId: isNew ? 0 : schedule.sheduleId,
      transportId: transportId,
      departFrom: formData.departFrom || null,
      departDate: formData.departDate ? moment(formData.departDate).format(formDateFormat) : null,
      departTime: formData.departTime || null,
      arrivedAt: formData.arrivedAt || null,
      arrivedDate: formData.arrivedDate ? moment(formData.arrivedDate).format(formDateFormat) : null,
      arrivedTime: formData.arrivedTime || null,
      mode: formData.mode || null,
      confirmed: formData.confirmed || null,
    };

    try {
      const url = SCHEDULE_URL;
      const authorization = authToken;

      const response = isNew
        ? await postStopApi({ url, payload, authorization })
        : await setStopApi({ url, payload, authorization });

      if (response.succeeded) {
        onSave();
        handleClose();
      } else {
        alert('Failed to save schedule');
      }
    } catch (error) {
      console.error('Error saving schedule:', error);
      alert('Error saving schedule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{isNew ? 'Add Schedule' : 'Edit Schedule'}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TransDropdownFormInput
                name="departFrom"
                control={control}
                options={organizations}
                organization={true}
                defaultId={defaultValues.departFrom}
                label="Depart From"
                register={register('departFrom')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TransDropdownFormInput
                name="arrivedAt"
                control={control}
                options={organizations}
                organization={true}
                defaultId={defaultValues.arrivedAt}
                label="Arrived At"
                register={register('arrivedAt')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormInputText
                name="departDate"
                type="date"
                control={control}
                shrink={true}
                label="Depart Date"
                register={register('departDate')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormInputText
                name="departTime"
                type="time"
                control={control}
                shrink={true}
                label="Depart Time"
                register={register('departTime')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormInputText
                name="arrivedDate"
                type="date"
                control={control}
                shrink={true}
                label="Arrived Date"
                register={register('arrivedDate')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormInputText
                name="arrivedTime"
                type="time"
                control={control}
                shrink={true}
                label="Arrived Time"
                register={register('arrivedTime')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TransDropdownFormInput
                name="mode"
                control={control}
                options={modeOptions}
                defaultId={defaultValues.mode}
                label="Mode"
                register={register('mode')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TransDropdownFormInput
                name="confirmed"
                control={control}
                options={confirmedOptions}
                defaultId={defaultValues.confirmed}
                label="Confirmed"
                register={register('confirmed')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ScheduleDialog;
